import styles from "../CategorizedNomenclatureListPage.module.scss";
import { useEffect, useMemo } from "react";
import {
  useForm,
  FormProvider,
  SubmitHandler,
  DefaultValues,
  FieldValues,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AppDialog } from "../../components/AppDialog/AppDialog";
import { DefaultInputField } from "../../components/forms/inputs/InputField/DefaultInputField";
import { DefaultButton } from "../../components/button/defaultButton/DefaultButton";
import { showCustomConfirmDialog } from "../../components/dialogs/AppConfirmDialog";
import { useFormValidationMessages } from "../../infrastructure/hooks/useFormHelper";
import { INomenclatureCategory } from "../models/categorizedNomenclatureModels";

export interface ICategoryFormModel {
  id?: number;
  name: string;
}

/**
 * Optional config that lets a caller replace the default (name-only) category form
 * with their own fields, schema, and defaults. Used by inventory categories to add
 * a required emoji field. When omitted, the dialog falls back to its original
 * name-only behavior so existing callers (ServiceType, etc.) continue working.
 */
export interface ICustomCategoryFormConfig<TCategoryFormModel extends FieldValues> {
  formFields: React.ReactNode;
  schema: yup.ObjectSchema<any>;
  defaultValues: () => DefaultValues<TCategoryFormModel>;
  /** How to map an existing category entity into form values for edit. */
  mapToFormValues?: (category: any) => DefaultValues<TCategoryFormModel>;
}

const defaultCategoryFormSchema = yup
  .object({
    id: yup.number().optional(),
    name: yup.string().required("Името е задължително"),
  })
  .required();

const getDefaultCategoryDefaults = (): DefaultValues<ICategoryFormModel> => ({
  name: "",
});

interface ICategoryFormDialogProps<
  TCategory extends INomenclatureCategory,
  TCategoryFormModel extends FieldValues = ICategoryFormModel
> {
  visible: boolean;
  editingCategoryId: number | null;
  categories: TCategory[];
  isSubmitting: boolean;
  onHide: () => void;
  onSubmit: (data: TCategoryFormModel) => void;
  /** Optional — provide to replace the default name-only form. */
  customConfig?: ICustomCategoryFormConfig<TCategoryFormModel>;
}

export const CategoryFormDialog = <
  TCategory extends INomenclatureCategory,
  TCategoryFormModel extends FieldValues = ICategoryFormModel
>({
  visible,
  editingCategoryId,
  categories,
  isSubmitting,
  onHide,
  onSubmit,
  customConfig,
}: ICategoryFormDialogProps<TCategory, TCategoryFormModel>) => {
  const schema = customConfig?.schema ?? defaultCategoryFormSchema;
  const defaults = useMemo<DefaultValues<TCategoryFormModel>>(
    () =>
      (customConfig?.defaultValues?.() ??
        (getDefaultCategoryDefaults() as DefaultValues<TCategoryFormModel>)),
    [customConfig]
  );

  const form = useForm<TCategoryFormModel>({
    resolver: yupResolver(schema) as any,
    defaultValues: defaults,
    mode: "onSubmit",
  });

  useFormValidationMessages<TCategoryFormModel>(form);

  const dialogTitle = editingCategoryId
    ? "Редактиране на категория"
    : "Добавяне на категория";

  useEffect(() => {
    if (visible) {
      if (editingCategoryId) {
        const category = categories.find((c) => c.id === editingCategoryId);
        if (category) {
          if (customConfig?.mapToFormValues) {
            form.reset(customConfig.mapToFormValues(category));
          } else {
            form.reset({
              id: category.id,
              name: category.name,
            } as unknown as DefaultValues<TCategoryFormModel>);
          }
        }
      } else {
        form.reset(defaults);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingCategoryId, categories]);

  const forceHide = () => {
    form.reset(defaults);
    onHide();
  };

  const handleHide = () => {
    if (form.formState.isDirty) {
      showCustomConfirmDialog(
        "Имате несъхранени промени. Сигурни ли сте, че искате да затворите?",
        forceHide,
        undefined,
        "warning",
        "Затвори",
        "Остани",
      );
      return;
    }
    forceHide();
  };

  const handleSubmit: SubmitHandler<TCategoryFormModel> = (data) => {
    onSubmit(data);
  };

  return (
    <AppDialog visible={visible} header={dialogTitle} onHide={handleHide}>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className={styles.category_form}
        >
          {customConfig ? (
            customConfig.formFields
          ) : (
            <DefaultInputField
              name="name"
              label="Име на категория"
              placeholder="Въведете име на категория"
              required
            />
          )}
          <div className={styles.category_form_actions}>
            <DefaultButton
              label="Отказ"
              design="outline"
              onClick={handleHide}
              type="button"
              width="120px"
            />
            <DefaultButton
              label="Запази"
              type="submit"
              loading={isSubmitting}
              width="120px"
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};

export { getDefaultCategoryDefaults as getCategoryDefaults };
